import { CommonModule } from '@angular/common';
import {
  Component,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonComponent, NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
} from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputGroupComponent, NzInputModule } from 'ng-zorro-antd/input';
import { interval, Subject, Subscription, take, takeUntil } from 'rxjs';
import {
  ApiResponse,
  AuthResult,
  LoginCredentials,
} from '../../../core/models/auth.models';
import { ErrorResponse } from '../../../core/models/error.models';
import { AuthService } from '../../../core/services/auth.service';
import { SecondsToTimePipe } from '../../../shared/pipes/seconds-to-time.pipe';
import { ReturnUrlService } from '../../../core/services/return-url.service';
import { OrganizationDetailModel } from '../../organizations/organization.interface';
import { environment } from '../../../../environments/environment.staging';

@Component({
  selector: 'app-verify-user',
  standalone: true,
  imports: [
    CommonModule,
    NzInputModule,
    NzButtonModule,
    NzIconModule,
    FormsModule,
    NzFormControlComponent,
    NzInputGroupComponent,
    NzFormItemComponent,
    NzButtonComponent,
    NzFormLabelComponent,
    SvgIconComponent,
    ReactiveFormsModule,
    SecondsToTimePipe,
  ],
  templateUrl: './verify-user.component.html',
  styleUrl: './verify-user.component.scss',
})
export class VerifyUserComponent implements OnInit, OnDestroy {
  isExpired = false;
  isResendOtp = false;
  timeRemaining = 120; // Start with 120 seconds
  timerSubscription: Subscription | null = null;
  errorMessage = '';
  isLoading = false;
  userEncrypt: string | null = localStorage.getItem('verify-user');
  user: LoginCredentials;
  private readonly destroy$ = new Subject<void>();
  orgData: OrganizationDetailModel | null = null;
  isOtpAlreadyGenerated = false;
  stageryLink: string | null = null;
  private readonly fb = inject(FormBuilder);
  readonly otpForm: FormGroup = this.fb.group({
    otp: [
      '',
      [
        Validators.required,
        Validators.pattern('^[0-9]*$'),
        Validators.minLength(6),
        Validators.maxLength(6),
      ],
    ],
  });
  fullReturnUrl: string | null = null;
  private readonly STORAGE_KEY = 'returnUrl';

  constructor(
    public router: Router,
    private readonly authService: AuthService,
    private readonly route: ActivatedRoute,
    private readonly returnUrlService: ReturnUrlService
  ) {
    // Check if OTP was already generated
    this.isOtpAlreadyGenerated =
      this.route.snapshot.queryParams['otpGenerated'] === 'true';
    this.stageryLink = this.route.snapshot.queryParams['stageryLink'] || null;
    if (!this.userEncrypt) {
      this.router.navigate(['/login']);
    }
    this.user = this.userEncrypt
      ? this.authService.decryptData(this.userEncrypt)
      : {};

    this.authService.orgData$.pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.orgData = res;
    });
    this.fullReturnUrl = this.route.snapshot.queryParams['returnUrl'];
    if (this.fullReturnUrl) {
      localStorage.setItem(
        this.STORAGE_KEY,
        decodeURIComponent(this.fullReturnUrl)
      );
    }
  }

  ngOnInit(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }

    if (this.isOtpAlreadyGenerated) {
      this.isExpired = true;
      this.errorMessage = 'OTP has been already sent. Please check your email.';
    } else {
      this.startOtpTimer();
    }

    const urlReturn = this.route.snapshot.queryParams['returnUrl'];
    if (urlReturn) {
      this.returnUrlService.setReturnUrl(urlReturn);
    }
  }

  startOtpTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }

    this.isExpired = false;
    this.timeRemaining = 120;

    this.timerSubscription = interval(1000)
      .pipe(take(this.timeRemaining))
      .subscribe({
        next: () => {
          this.timeRemaining--;
        },
        complete: () => {
          this.isExpired = true;
          console.log('OTP expired');
        },
      });
  }

  resendOtp(): void {
    this.isResendOtp = true;
    this.isOtpAlreadyGenerated = false;
    this.errorMessage = '';

    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }

    this.otpForm.reset();

    const queryParams = { ...this.route.snapshot.queryParams };
    delete queryParams['otpGenerated'];
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
    this.authService
      .sentOtp(this.user)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isExpired = false;
          this.errorMessage = '';
          this.startOtpTimer();
          setTimeout(() => {
            const otpInput = document.getElementById('otp');
            if (otpInput instanceof HTMLInputElement) {
              otpInput.focus();
            }
          }, 100);
        },
        error: (error: ErrorResponse) => {
          console.log('Error resending OTP:', error);
          this.isResendOtp = false;
          if (error.errors && error.errors[0]?.field === 'OTP_ALREADY_SENT') {
            this.errorMessage =
              'OTP has been already sent. Please check your email.';
            this.startOtpTimer();
          } else {
            this.isExpired = true;
            this.errorMessage =
              error.message || 'Failed to send OTP. Please try again.';
          }
        },
        complete: () => {
          this.isResendOtp = false;
        },
      });
  }
  private readonly USER_KEY = 'user_data';

  signIn() {
    if (!this.user || this.otpForm.invalid) {
      return;
    }
    this.isLoading = true;
    this.errorMessage = '';

    this.authService
      .login({ email: this.user.email, otp: this.otpForm.value.otp })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: ApiResponse<AuthResult>) => {
          localStorage.removeItem('verify-user');
          localStorage.removeItem('isOtpVerified');
          const returnUrl = this.returnUrlService.getReturnUrl();
          if (res.result.isSuperAdmin) {
            this.returnUrlService.clearReturnUrl();
            const routeURL = returnUrl
              ? returnUrl !== this.router.url
                ? returnUrl
                : '/activity-dashboard'
              : '/activity-dashboard';
            this.router.navigateByUrl(routeURL);
          } else {
            const user = localStorage.getItem(this.USER_KEY);
            if (user) {
              this.authService.userData.next(
                this.authService.decryptData(user)
              );
            }
            const roles = res.result.roles || [];
            this.authService.setRole(roles || []).then(() => {
              this.authService.logoutLMS();
              const returnUrl = this.returnUrlService.getReturnUrl();
              this.returnUrlService.clearReturnUrl();
              const url = this.stageryLink
                ? this.stageryLink
                : returnUrl
                  ? returnUrl !== this.router.url
                    ? returnUrl
                    : '/dashboard'
                  : '/dashboard';
              this.router.navigateByUrl(url);
            });
          }
        },
        error: (error: ErrorResponse) => {
          this.isLoading = false;
          this.errorMessage = error.errors?.[0]?.description
            ? error.errors[0].description
            : 'Invalid OTP. Please try again or request a new OTP.';

          const currentValue = this.otpForm.value.otp;
          this.otpForm.reset({ otp: currentValue });
          this.otpForm.markAsTouched();
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  onInputFocus(event: FocusEvent): void {
    if (event.target instanceof HTMLInputElement) {
      event.target.select();
    }
  }

  clearError(): void {
    this.errorMessage = '';
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (!environment.production) {
      console.warn(event);
    }
    localStorage.removeItem('verify-user');
  }

  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
      this.timerSubscription = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
  }
}
